import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from '@resy/ui-kitchen';
import { setScrolledActiveSection, setHashValueWithOffset, scrollTo } from '../../AmexMarketingUtils/AmexUtils';
import AmexSecurityCenterAiIntegrations from '../AmexSecurityCenterAiIntegrations';
import AmexExpandableSections from '../../AmexMarketingUtils/AmexExpandableSections';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import './AmexSecurityCenterBrokerActivity.scss';

const AmexSecurityCenterBrokerActivity = (
  {
    awsFolderName,
    baseRequestUrl,
    brokerActivityConfig: {
      icon: sectionIcon = '',
      id: sectionId = '',
      title = '',
      activity_list: activityList = {},
    },
    expandableConfig = {},
    aiIntegrationConfig,
    matchMedia,
    dispatch,
    hash,
    updateHash,
    eventAnalytics,
    isSecurityCenter2026Enabled,
  }
) => {
  const [linkClicked, setLinkClicked] = useState(null);
  const urlPrefix = `${baseRequestUrl}${awsFolderName}/images/`;
  const icon = `${urlPrefix}${sectionIcon}`;
  const {
    hide_section: hideActivityListSection,
    activity_title: activityTitle = '',
    list_icon: listIcon = '',
    list_icon_2026: listIcon2026 = '',
    activity_items: activityItems = [],
  } = activityList;
  const hideActivityList = hideActivityListSection || activityItems.length === 0;
  const hideExpandableSections = expandableConfig?.hide_section;
  const hideAiIntegrations = aiIntegrationConfig?.hide_section;
  const isMobile = !matchMedia.md;
  const finalListIcon = isSecurityCenter2026Enabled ? listIcon2026 : listIcon;
  setHashValueWithOffset(isMobile, 210, 180, sectionId, hash, linkClicked);
  const sections = useRef([]);

  const sanitizedTitle = sanitizeString(title);
  const sanitizedActivityTitle = sanitizeString(activityTitle);

  useEffect(() => {
    sections.current = document.querySelectorAll('[data-section]');
    window.addEventListener('scroll', () => { setLinkClicked(null); setScrolledActiveSection(sections, dispatch); });

    return () => {
      window.removeEventListener('scroll', () => { setScrolledActiveSection(sections, dispatch); });
    };
  }, []);

  const renderActivityListItem = (item) => {
    const {
      copy_1: copyPart1 = '',
      copy_2: copyPart2 = '',
      link = {},
    } = item;
    const {
      url,
      aria_label: linkAria,
    } = link;
    if (!copyPart1) return false;

    const sanitizedCopyPart1 = sanitizeString(copyPart1);
    const sanitizedCopyPart2 = sanitizeString(copyPart2);

    return (
      <li
        key={item}
        className="AmexSecurityCenterBrokerActivity__activityList--item"
      >
        {finalListIcon && (
          <img
            className="AmexSecurityCenterBrokerActivity__activityList--icon"
            src={`${urlPrefix}${finalListIcon}`}
            alt=""
          />
        )}
        {copyPart1 && (
          <span
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedCopyPart1,
            }}
          />
        )}
        {Object.keys(link).length > 0 && (
          <Link
            href={window?.location?.href?.split('#')[0] + url}
            aria-label={linkAria}
            className="AmexSecurityCenterBrokerActivity__activityList--link"
            onClick={() => {
              updateHash(url);
              scrollTo(url);
              setLinkClicked(url);
            }}
          >
            {link.label}
          </Link>
        )}
        {copyPart2 && (
          <span
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedCopyPart2,
            }}
          />
        )}
      </li>
    );
  };

  return (
    <div
      data-section
      id={`${sectionId}_section`}
    >
      <section
        className="AmexSecurityCenterBrokerActivity"
      >
        <div className="container">
          {sectionIcon && (
            <img
              className="AmexSecurityCenterBrokerActivity__icon"
              src={icon}
              alt=""
            />
          )}
          {title && (
            <h2
              id={sectionId}
              className="AmexSecurityCenterBrokerActivity__title"
              dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                __html: sanitizedTitle,
              }}
            />
          )}
          {!hideActivityList && (
            <div className="AmexSecurityCenterBrokerActivity__activityList">
              {activityList && (
                <h3
                  className="AmexSecurityCenterBrokerActivity__activityList--title"
                  dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                    __html: sanitizedActivityTitle,
                  }}
                />
              )}
              <ul>
                {activityItems && activityItems.map((item) => renderActivityListItem(item))}
              </ul>
            </div>
          )}
        </div>
      </section>
      {(!hideAiIntegrations && isSecurityCenter2026Enabled) && (
        <AmexSecurityCenterAiIntegrations
          awsFolderName={awsFolderName}
          baseRequestUrl={baseRequestUrl}
          aiIntegrationConfig={aiIntegrationConfig}
          hash={hash}
        />
      )}
      {!hideExpandableSections && (
      <AmexExpandableSections
        awsFolderName={awsFolderName}
        baseRequestUrl={baseRequestUrl}
        matchMedia={matchMedia}
        expandableConfig={expandableConfig}
        eventAnalytics={eventAnalytics}
      />
      )}
    </div>
  );
};

AmexSecurityCenterBrokerActivity.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  brokerActivityConfig: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    activity_list: PropTypes.shape({}).isRequired,
  }).isRequired,
  expandableConfig: PropTypes.shape({}).isRequired,
  aiIntegrationConfig: PropTypes.shape({
    hide_section: PropTypes.bool.isRequired,
  }).isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  eventAnalytics: PropTypes.func.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  updateHash: PropTypes.func.isRequired,
  hash: PropTypes.string.isRequired,
  isSecurityCenter2026Enabled: PropTypes.bool,
};

AmexSecurityCenterBrokerActivity.defaultProps = {
  isSecurityCenter2026Enabled: false,
};

export { AmexSecurityCenterBrokerActivity as ComponentUnderTest };
export default connect()(AmexSecurityCenterBrokerActivity);
